import { useCallback, useEffect, useRef, useState } from "react";
import {
  getSeedJob,
  getSeedStatus,
  SeedError,
  seedErrorText,
  seedRetryable,
  startSeed,
  type SeedJob,
  type SeedStatus,
} from "../api/bobSeed";

type Props = {
  project: string;
  isAdmin: boolean;
  /** Called when a review this control watched ends with findings recorded. */
  onDone: () => void | Promise<void>;
  pollMs?: number;
  /** How long the control polls at `pollMs` before it says the review is still
   *  running and polls every `slowPollMs` instead. */
  ceilingMs?: number;
  slowPollMs?: number;
  /** Bumped by the ledger's Refresh: re-read the status and pick up a live job. */
  refreshKey?: number;
};

const DEFAULT_POLL_MS = 2000;
const DEFAULT_CEILING_MS = 20 * 60_000;
const DEFAULT_SLOW_POLL_MS = 30_000;

const PILL: Record<string, string> = {
  running: "running",
  queued: "queued",
  cooldown: "cooling down",
  daily_cap: "daily cap reached",
  queue_full: "Bob's queue is full",
  seeding_off: "Full reviews are turned off for this pack",
  idle: "idle",
  checking: "checking",
  unavailable: "unavailable",
};

/** The states Bob's status route sends. Anything else is read as unavailable, never as idle. */
const STATUS_STATES = new Set(["running", "queued", "cooldown", "daily_cap", "queue_full", "seeding_off", "idle"]);

/** Failed polls in a row before the control says Bob is not answering and polls slowly. */
const SILENT_AFTER = 5;

const BLOCKING = new Set([
  "running", "queued", "cooldown", "daily_cap", "queue_full", "seeding_off", "checking", "unavailable",
]);

/** "in 2h 5m" until an ISO time, or "". Minutes round up, so a slot never reads earlier than it is. */
export function untilText(iso: string | null | undefined, now = Date.now()): string {
  if (typeof iso !== "string") return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t) || t <= now) return "";
  const min = Math.ceil((t - now) / 60_000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `in ${h}h ${m}m` : `in ${m}m`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

type Phase = "idle" | "starting" | "polling" | "done" | "partial" | "still_running" | "silent" | "error";

/** Bob ends a seed whose budget ran out before it finished adjudicating as
 *  `failed` with this prefix. Its findings are recorded. */
const PARTIAL_SEED = /^partial seed:\s*/;

/**
 * The ledger header's "Review whole pack" control, admin only. It starts one of
 * Bob's full-pack seed reviews and polls the job until it ends. A run id drops
 * stale answers; a failure `seedRetryable` accepts is retried, any other ends
 * the watch. Past the ceiling, or after SILENT_AFTER failures in a row, polls
 * slow down. A start is never forced on its own: during a cooldown a separate
 * "Run anyway" lifts the cooldown and nothing else.
 */
export default function SeedReviewControl(
  {
    project,
    isAdmin,
    onDone,
    pollMs = DEFAULT_POLL_MS,
    ceilingMs = DEFAULT_CEILING_MS,
    slowPollMs = DEFAULT_SLOW_POLL_MS,
    refreshKey = 0,
  }: Props,
) {
  const [status, setStatus] = useState<SeedStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [job, setJob] = useState<SeedJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<"start" | "force" | null>(null);
  const runId = useRef(0);
  const phaseRef = useRef<Phase>("idle");
  phaseRef.current = phase;
  const statusGen = useRef(0);
  const alive = useRef(true);
  // A ref, so a new `onDone` does not restart a running watch.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const readStatus = useCallback(async (): Promise<SeedStatus | null> => {
    const gen = statusGen.current;
    try {
      const st = await getSeedStatus(project);
      if (!alive.current || gen !== statusGen.current) return null;
      setStatus(st);
      setStatusError(null);
      return st;
    } catch (e) {
      if (alive.current && gen === statusGen.current) {
        setStatus(null);
        setStatusError(seedErrorText(e));
      }
      return null;
    }
  }, [project]);

  const watch = useCallback(async (my: number, first: SeedJob) => {
    setJob(first);
    setPhase("polling");
    const start = Date.now();
    let slow = false;
    let failures = 0;
    const afterPoll = () => {
      const pastCeiling = Date.now() - start >= ceilingMs;
      const silent = failures >= SILENT_AFTER;
      slow = pastCeiling || silent;
      setPhase(silent ? "silent" : pastCeiling ? "still_running" : "polling");
    };
    for (;;) {
      await sleep(slow ? slowPollMs : pollMs);
      if (!alive.current || runId.current !== my) return;
      let j: SeedJob | null = null;
      try {
        j = await getSeedJob(project, first.id);
      } catch (e) {
        if (!alive.current || runId.current !== my) return;
        if (!seedRetryable(e)) {
          setError(seedErrorText(e));
          setPhase("error");
          void readStatus();
          return;
        }
        failures += 1;
      }
      if (!alive.current || runId.current !== my) return;
      if (j === null) {
        afterPoll();
        continue;
      }
      failures = 0;
      setJob(j);
      if (j.state === "done") {
        setPhase("done");
        void readStatus();
        await onDoneRef.current();
        return;
      }
      if (j.state === "failed" && typeof j.error === "string" && PARTIAL_SEED.test(j.error)) {
        setError(j.error.replace(PARTIAL_SEED, ""));
        setPhase("partial");
        void readStatus();
        await onDoneRef.current();
        return;
      }
      if (j.state === "failed" || j.state === "refused") {
        setError(
          j.state === "refused"
            ? "Bob was busy with another pass the whole time, so this review never started and spent nothing."
            : `The review failed${j.error ? `: ${j.error}` : "."}`,
        );
        setPhase("error");
        void readStatus();
        return;
      }
      afterPoll();
    }
  }, [project, pollMs, ceilingMs, slowPollMs, readStatus]);

  // Once per project, picking a live job back up.
  useEffect(() => {
    if (!isAdmin) return;
    const my = ++runId.current;
    statusGen.current += 1;
    setStatus(null);
    setStatusError(null);
    setPhase("idle");
    setJob(null);
    setError(null);
    setConfirming(null);
    void readStatus().then((st) => {
      if (!st || !alive.current || runId.current !== my) return;
      if (st.job && (st.job.state === "queued" || st.job.state === "running")) void watch(my, st.job);
    });
  }, [isAdmin, project, readStatus, watch]);

  useEffect(() => {
    if (!isAdmin || refreshKey === 0) return;
    void readStatus().then((st) => {
      if (!st?.job || !alive.current) return;
      if (st.job.state !== "queued" && st.job.state !== "running") return;
      if (phaseRef.current === "polling" || phaseRef.current === "starting") return;
      void watch(++runId.current, st.job);
    });
    // Only a new refreshKey asks; the project effect above covers a switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const start = useCallback(async (force: boolean) => {
    setConfirming(null);
    const my = ++runId.current;
    setError(null);
    setJob(null);
    setPhase("starting");
    let first: SeedJob;
    try {
      first = await startSeed(project, force);
    } catch (e) {
      if (!alive.current || runId.current !== my) return;
      const body = e instanceof SeedError ? e.body : {};
      // A seed of this pack is already live and Bob sent it: watch that one.
      if (body.job && typeof body.job === "object") {
        void watch(my, body.job as SeedJob);
        return;
      }
      setError(seedErrorText(e));
      setPhase("error");
      void readStatus();
      return;
    }
    if (!alive.current || runId.current !== my) return;
    void readStatus();
    void watch(my, first);
  }, [project, watch, readStatus]);

  if (!isAdmin) return null;

  const watching = phase === "polling" || phase === "starting" || phase === "still_running" || phase === "silent";
  const unknownState = status !== null && !STATUS_STATES.has(status.state);
  const pillState = phase === "silent"
    ? "unavailable"
    : watching
      ? (job?.state === "running" ? "running" : "queued")
      : statusError !== null || unknownState ? "unavailable" : (status?.state ?? "checking");
  const unavailableNote = statusError ?? (unknownState ? "Bob sent a status this page does not know." : null);
  const label = PILL[pillState] ?? PILL.idle!;
  const slot = pillState === "cooldown" || pillState === "daily_cap" ? untilText(status?.retry_at) : "";
  const blocked = watching || BLOCKING.has(pillState);
  const canForce = !watching && pillState === "cooldown";
  const attempts = status?.attempts_24h;
  const cap = status?.limits?.daily_cap;
  const runOfCap = typeof attempts === "number" && typeof cap === "number" && cap > 0
    ? ` This is run ${attempts + 1} of ${cap} allowed in 24 hours.`
    : "";
  const cost = "A review costs up to about $15, the configured ceiling, and runs for several minutes.";

  return (
    <span className="cr-seed" data-seed={pillState}>
      <span className="cr-seed-pill" role="status" title={status?.retry_at ?? undefined}>
        {(pillState === "running" || pillState === "queued") && <span className="bob-pulse" aria-hidden="true" />}
        Whole-pack review: {label}
        {pillState === "queued" && job?.position ? ` (position ${job.position})` : ""}
        {slot ? `, next slot opens ${slot}` : ""}
      </span>
      {watching && typeof job?.cost_usd === "number" && (
        <span className="cr-seed-spend" title="What this review has spent so far">${job.cost_usd.toFixed(2)} spent</span>
      )}
      <button
        type="button"
        className="bob-btn cr-seed-start"
        disabled={blocked}
        aria-expanded={confirming === "start"}
        title={pillState === "daily_cap"
          ? "Bob has run as many whole-pack reviews of this pack as he may today. Run anyway does not lift this."
          : "Bob reviews every file in the pack, not only what changed."}
        onClick={() => setConfirming(confirming === "start" ? null : "start")}
      >
        {phase === "starting" ? "Starting…" : "Review whole pack"}
      </button>
      {canForce && (
        <button
          type="button"
          className="bob-btn cr-seed-force"
          aria-expanded={confirming === "force"}
          onClick={() => setConfirming(confirming === "force" ? null : "force")}
        >
          Run anyway (skip cooldown)
        </button>
      )}
      {confirming === "start" && (
        <span className="cr-confirm" role="dialog" aria-label="Start a whole-pack review">
          <span>
            Bob reviews every file in this pack. {cost}
            {status?.seeded === true ? " Bob has reviewed this pack before; this runs another full pass." : ""}
            {runOfCap}
          </span>
          <button type="button" className="bob-btn" onClick={() => void start(false)}>Start review</button>
          <button type="button" className="bob-btn" onClick={() => setConfirming(null)}>Cancel</button>
        </span>
      )}
      {confirming === "force" && (
        <span className="cr-confirm" role="dialog" aria-label="Run a whole-pack review during the cooldown">
          <span>
            This review skips the cooldown between whole-pack reviews. The daily cap still applies. {cost}
            {runOfCap}
          </span>
          <button type="button" className="bob-btn" onClick={() => void start(true)}>Run anyway</button>
          <button type="button" className="bob-btn" onClick={() => setConfirming(null)}>Cancel</button>
        </span>
      )}
      {!watching && pillState === "unavailable" && unavailableNote && <span className="cr-seed-note">{unavailableNote}</span>}
      {phase === "done" && <span className="cr-seed-note">Whole-pack review finished. The ledger below is fresh.</span>}
      {phase === "partial" && <span className="cr-seed-note">Finished partially: {error}</span>}
      {phase === "silent" && (
        <span className="cr-seed-note">Bob is not answering. This page checks again every so often.</span>
      )}
      {phase === "still_running" && (
        <span className="cr-seed-note">
          Still running. Bob carries on, and this page checks again every so often.
        </span>
      )}
      {phase === "error" && error && <span className="cr-error-line" role="alert">{error}</span>}
    </span>
  );
}
